'use client';

import CloseIcon from '@/components/icons/Close';
import { cn } from '@/lib/ui/cn';
import { useEffect } from 'react';

const DEFAULT_BACKGROUND_COLOR = 'bg-sh-white';

interface ModalProps {
  backgroundColor?: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  contentClassName?: string;
}

export function Modal({
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  isOpen,
  onClose,
  children,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
  contentClassName,
}: ModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && closeOnEscape) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) onClose();
  };

  return (
    <div
      className={cn(
        'bg-sh-black/30 fixed inset-0 z-55 flex items-center justify-center',
        className
      )}
      onClick={handleOverlayClick}
    >
      <div
        className={cn(
          'relative mx-4 w-full max-w-2xl shadow-xl lg:max-w-3xl xl:max-w-6xl',
          backgroundColor,
          contentClassName
        )}
        onClick={e => e.stopPropagation()}
      >
        {children}
        {showCloseButton && (
          <button
            onClick={onClose}
            className='absolute top-4 right-4 cursor-pointer text-gray-400 hover:text-gray-600'
            aria-label='Close modal'
          >
            <CloseIcon className='size-6' strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}
